import { Injectable } from "@angular/core";
import { UserSession } from "@modules/api/user-session.generated";
import { HttpErrorService } from "@modules/common/services/http-error.service";
import { RefreshSessionService } from '@modules/common/services/refresh-session.service';
import { UserSessionService } from "@modules/common/services/user-session.service";
import * as moment from 'moment';
import { Observable, of as observableOf } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationTokenService {
    constructor(
        private _userSessionService: UserSessionService,
        private _refreshSessionService: RefreshSessionService,
        private _httpErrorService: HttpErrorService
    ) { }

    public getAuthenticationToken(): Observable<string | undefined> | undefined {
        let session = this._userSessionService.getUserSession();
        if (this.isValid(session)) {
            if (this.requiresRefresh(session!)) {
                return this._refreshSessionService.refreshSession({
                    authenticationToken: session!.authenticationToken!.token!,
                    refreshToken: session!.refreshToken!
                }).pipe(tap(() => { }, x => {
                    this._httpErrorService.handleHttpError(x);
                }),map((result: UserSession) => {
                    if (result) {
                        this._userSessionService.setUserSession(result);
                    } else {
                        //One of the reasons refreshSession() can return null
                        //is that a different tab tried to refresh the token
                        //at the same time (this happens a lot when debugging).
                        //In that case, it's cross your fingers time and we
                        // try to get the session from the other tab.
                        session = this._userSessionService.getUserSession();
                    }
                    return result && result.authenticationToken && result.authenticationToken.token;
                }),);
            }

            return observableOf(session!.authenticationToken!.token!);
        }
    }

    public hasValidAuthenticationToken(): boolean {
        return this.isValid(this._userSessionService.getUserSession());
    }

    private isValid(session?: UserSession): boolean {
        return !!(session
            && session.authenticationToken
            && session.authenticationToken.issuedAt
            && session.authenticationToken.expiresAt
            && moment.utc(session.authenticationToken.expiresAt).diff(moment.utc()) > 0);
    }

    private requiresRefresh(session: UserSession): boolean {
        let validTimespan = moment.utc(session.authenticationToken!.expiresAt).diff(moment.utc(session.authenticationToken!.issuedAt));
        return moment.utc().diff(moment.utc(session.authenticationToken!.issuedAt)) > validTimespan / 2;
    }
}
